import { Link, useRoute } from "wouter";
import { CarLogo } from "../components/common/CarLogo.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

/**
 * F-chantier-detail — visão completa do CHANTIER no ERP (clique no card):
 * clientes vinculados, dossiers/veículos (com recepção/entrega/fatura),
 * devis "a executar" e a agenda dos próximos dias.
 */

export default function ChantierDetail() {
  const t = useT();
  const { user } = useAuth();
  const [, params] = useRoute("/chantiers/:id");
  const chantierId = Number(params?.id ?? 0);
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId] = useSelectedCompany(accessibleCompanies);
  const detailQuery = trpc.chantiers.detail.useQuery(
    { companyId: companyId ?? 0, chantierId },
    { enabled: companyId != null && chantierId > 0 },
  );

  if (!user) return null;
  const d = detailQuery.data;

  const statusChip = (label: string, on: boolean) => (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] ${
        on ? "bg-lime/15 text-lime" : "bg-neutral-800 text-neutral-500"
      }`}
    >
      {label}
    </span>
  );

  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-5">
        {detailQuery.isLoading && <p className="text-sm text-neutral-500">…</p>}
        {detailQuery.error && <p className="text-sm text-red-400">{detailQuery.error.message}</p>}
        {d && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold tracking-wide">🏗 {d.chantier.name}</h1>
                <p className="text-sm text-neutral-500">
                  {[d.chantier.address, d.chantier.postalCode, d.chantier.city]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                  {(d.chantier.startDate || d.chantier.endDate) &&
                    ` · ${d.chantier.startDate ?? "…"} → ${d.chantier.endDate ?? "…"}`}
                  {!d.chantier.active && ` · ${t("chantiers.inactiveWord")}`}
                </p>
              </div>
              <Link
                href="/chantiers"
                className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm hover:border-lime"
              >
                ← {t("chantiers.title")}
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              {(
                [
                  [t("chantiers.customersWord"), d.customers.length],
                  [t("chantiers.detailCarsWord"), d.dossiers.length],
                  [
                    t("chantiers.detailDevisWord"),
                    d.dossiers.filter((x) => x.source === "edsp_devis_pdf" && !x.hasLines).length,
                  ],
                  [t("chantiers.detailAgendaWord"), d.appointments.length],
                ] as const
              ).map(([label, n]) => (
                <div
                  key={label}
                  className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-3 text-center"
                >
                  <div className="text-2xl font-bold">{n}</div>
                  <div className="text-xs text-neutral-500">{label}</div>
                </div>
              ))}
            </div>

            {/* Agenda */}
            <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                📅 {t("chantiers.detailAgendaTitle")}
              </h2>
              {d.appointments.length === 0 && (
                <p className="text-sm text-neutral-600">{t("chantiers.detailAgendaEmpty")}</p>
              )}
              <div className="grid gap-1.5 md:grid-cols-2">
                {d.appointments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-950/50 px-3 py-1.5 text-sm"
                  >
                    <span className="min-w-0 truncate">
                      <span className="font-mono">{a.date}</span>
                      <span className="font-semibold text-lime"> {a.time}</span>
                      {a.plate && <span className="font-mono"> · {a.plate}</span>}
                      {a.vehicleLabel && (
                        <span className="text-neutral-400"> · {a.vehicleLabel}</span>
                      )}
                      {a.customerName && (
                        <span className="text-neutral-500"> · {a.customerName}</span>
                      )}
                    </span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                        a.status === "scheduled"
                          ? "bg-sky-500/15 text-sky-300"
                          : a.status === "arrived"
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-lime/15 text-lime"
                      }`}
                    >
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Clientes */}
            <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                👥 {t("chantiers.detailCustomersTitle")}
              </h2>
              {d.customers.length === 0 && (
                <p className="text-sm text-neutral-600">{t("chantiers.detailCustomersEmpty")}</p>
              )}
              <div className="grid gap-1.5 md:grid-cols-2">
                {d.customers.map((c) => (
                  <div
                    key={c.id}
                    className={`flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-950/50 px-3 py-1.5 text-sm ${
                      c.active ? "" : "opacity-60"
                    }`}
                  >
                    <span className="min-w-0 truncate">
                      {c.displayName}
                      {(c.email || c.phone) && (
                        <span className="text-xs text-neutral-500"> · {c.email ?? c.phone}</span>
                      )}
                    </span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                        c.kind === "individual"
                          ? "bg-red-500/15 text-red-300"
                          : "bg-blue-500/15 text-blue-300"
                      }`}
                    >
                      {c.kind === "individual"
                        ? t("customers.kindIndividual")
                        : t("customers.kindBusiness")}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Dossiers / veículos */}
            <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                🚗 {t("chantiers.detailCarsTitle")}
              </h2>
              {d.dossiers.length === 0 && (
                <p className="text-sm text-neutral-600">{t("chantiers.detailCarsEmpty")}</p>
              )}
              <div className="space-y-1.5">
                {d.dossiers.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-950/50 px-3 py-1.5 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <CarLogo make={v.vehicleMakeSnapshot} size={18} />
                      <span className="min-w-0 truncate">
                        <Link
                          href={`/dossiers/${v.id}`}
                          className="font-mono text-lime hover:underline"
                        >
                          {v.dossierCode}
                        </Link>
                        <span className="font-mono"> · {v.plate ?? v.vin ?? "—"}</span>
                        <span className="text-neutral-400">
                          {" "}
                          ·{" "}
                          {[v.vehicleMakeSnapshot, v.vehicleModelSnapshot]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </span>
                        <span className="text-neutral-500"> · {v.customerName}</span>
                        {v.source === "edsp_devis_pdf" && !v.hasLines && (
                          <span className="ml-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300">
                            {t("chantiers.devisChip")}
                            {v.externalDevisRef ? ` ${v.externalDevisRef}` : ""}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      {v.franchise && (
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                          {t("chantier.franchiseLabel")}
                        </span>
                      )}
                      {statusChip("📥", v.received)}
                      {statusChip("📤", v.delivered)}
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          v.customerInvoiceStatus === "paid"
                            ? "bg-lime/15 text-lime"
                            : v.customerInvoiceStatus === "issued"
                              ? "bg-sky-500/15 text-sky-300"
                              : "bg-neutral-800 text-neutral-400"
                        }`}
                      >
                        {v.customerInvoiceStatus}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </ErpShell>
  );
}
